// 머리글 줄 찾기와 열 매핑 추정 — 격자(string[][])만 본다.

import {
  ColumnMap,
  HEADER_SCAN_ROWS,
  HeaderGuess,
  ImportField,
  ImportParse,
} from "./types";
import { VatMode } from "../../money/vat";
import { parseNumber } from "../editor/cell-number";

/** 머리글 비교용. 공백·괄호·구두점을 걷어 낸다. */
export const normLabel = (text: string): string =>
  text.replace(/[\s()[\]{}·.,:\-_/\\*※]/g, "").toLowerCase();

/** 원가 단가 열 머리글인가 — 「원가」「원가단가」「원가 단가」 · 「원가 금액」·「원가 합계」·「원가율」·「원가대비」·「원가(%)」 는 아니다. 붙여넣기도 이 한 벌을 쓴다. */
export const isCostLabel = (text: string): boolean => {
  const t = text.replace(/[\s()[\]{}·.,:\-_/\\*※]/g, "");
  return t.includes("원가") && !/(금액|합계|총액|소계|공급액|원가계$|%|율|대비)/.test(t);
};

const has = (text: string, words: string[]): boolean => words.some((w) => text.includes(w));

type Guess = ImportField | "price" | null;

// 머리글 글자 하나를 채울 곳으로. 「단가」 는 앞뒤 순서로 나중에 가른다.
const guessOne = (raw: string): Guess => {
  const t = normLabel(raw);
  if (t === "") return null;
  if (/^(no|번호|순번|순서|연번)$/.test(t)) return null;
  if (has(t, ["비고", "메모", "참고", "특기"])) return "note";
  if (isCostLabel(t)) return "cost_unit_price";
  // 「원가 금액」·「원가 합계」 는 담지도 대조하지도 않는다 — 판매 금액 열로 잡히면 안 된다.
  if (t.includes("원가")) return null;
  if (t.includes("단가")) {
    if (has(t, ["자재", "재료"])) return "material_unit_price";
    if (has(t, ["노무", "인건", "노임", "시공비"])) return "labor_unit_price";
    if (has(t, ["경비", "기타"])) return "expense_unit_price";
    return "price";
  }
  if (has(t, ["금액", "공급액", "공급가", "합계", "총액", "소계"]) || t === "계") {
    if (has(t, ["자재", "재료"])) return "material_amount";
    if (has(t, ["노무", "인건", "노임"])) return "labor_amount";
    return "amount_total";
  }
  // 「자재비」「노무비」 처럼 단가·금액 말 없이 금액만 적힌 열.
  if (has(t, ["자재비", "재료비"])) return "material_amount";
  if (has(t, ["노무비", "인건비", "노임", "시공비"])) return "labor_amount";
  if (t.includes("수량")) return "qty";
  if (has(t, ["규격", "단위"])) return "unit";
  if (has(t, ["공간", "실명"]) || t === "실") return "space_code";
  if (has(t, ["범위", "부위", "위치", "세부", "중공정", "중분류", "구분"])) return "scope";
  if (has(t, ["작업내역", "품명", "내용", "내역", "항목", "품목", "작업명", "공사명", "명칭"])) {
    return "item";
  }
  if (has(t, ["공정", "공종", "대분류"])) return "trade";
  return null;
};

// 자재·노무 금액 열은 그 단가 열이 없을 때만 단가를 구하는 데 쓰고, 있으면 대조용으로 둔다.
const AMOUNT_PRICE: Partial<Record<ImportField, ImportField>> = {
  material_amount: "material_unit_price",
  labor_amount: "labor_unit_price",
};

const resolveAmounts = (columns: ColumnMap): ColumnMap => {
  const used = new Set<ImportField>();
  return columns.map((f) => {
    const price = f === null ? undefined : AMOUNT_PRICE[f];
    if (f === null || price === undefined) return f;
    if (columns.includes(price) || used.has(f)) return "amount_part";
    used.add(f);
    return f;
  });
};

const guessBase = (labels: string[]): ColumnMap => {
  const guesses = labels.map(guessOne);
  const taken = new Set<ImportField>();
  // 「규격」 과 「단위」 가 둘 다 있으면 단위가 이긴다.
  const unitCols = labels
    .map((l, i) => (guesses[i] === "unit" ? i : -1))
    .filter((i) => i >= 0);
  const unitWinner =
    unitCols.find((i) => normLabel(labels[i]).includes("단위")) ?? unitCols[0] ?? -1;
  // 이름이 붙은 단가를 먼저 자리 잡게 한다.
  guesses.forEach((g) => {
    if (g === "material_unit_price" || g === "labor_unit_price") taken.add(g);
  });
  const named = new Set<ImportField>();

  return guesses.map((g, i) => {
    if (g === null) return null;
    if (g === "unit" && i !== unitWinner) return null;
    if (g === "amount_part" || g === "material_amount" || g === "labor_amount") return g;
    if (g === "price") {
      const field = !taken.has("material_unit_price")
        ? "material_unit_price"
        : !taken.has("labor_unit_price")
          ? "labor_unit_price"
          : null;
      if (field) taken.add(field);
      if (field) named.add(field);
      return field;
    }
    if (g === "material_unit_price" || g === "labor_unit_price") {
      if (named.has(g)) return null;
      named.add(g);
      return g;
    }
    if (named.has(g)) return null;
    named.add(g);
    return g;
  });
};

/** 머리글 글자들을 열 매핑으로. 같은 곳을 두 열이 채우면 앞 열만 남긴다(단가는 앞=자재·뒤=노무). */
export const guessColumns = (labels: string[]): ColumnMap => resolveAmounts(guessBase(labels));

// 매핑이 얼마나 머리글다운가 — 채운 곳의 가짓수. 작업내역·수량 중 하나는 있어야 한다.
const scoreOf = (columns: ColumnMap): number => {
  const fields = new Set(columns.filter((c): c is ImportField => c !== null));
  if (!fields.has("item") && !fields.has("qty")) return 0;
  return fields.size;
};

// 머리글이 없으면 붙여넣기와 같은 열 순서로 본다(PASTE_COLUMNS).
const NO_HEADER_COLUMNS: ImportField[] = [
  "item",
  "unit",
  "qty",
  "material_unit_price",
  "labor_unit_price",
  "note",
];

const SUBHEAD_WORDS = ["단가", "금액", "공급액", "공급가"];

// 위 줄의 값을 오른쪽 빈칸으로 이어 준다 — 병합된 「자재비」 가 두 칸을 덮는 경우.
const carryRight = (row: string[], width: number): string[] => {
  const out: string[] = [];
  let last = "";
  for (let c = 0; c < width; c += 1) {
    const v = (row[c] ?? "").trim();
    if (v !== "") last = v;
    out.push(v === "" ? last : v);
  }
  return out;
};

/** 격자의 가장 넓은 줄 폭. */
export const widthOf = (grid: string[][]): number =>
  grid.reduce((w, row) => Math.max(w, row.length), 0);

/** 머리글 줄 `row` 를 기준으로 글자·매핑을 만든다. 아래 줄이 「단가·금액」 이면 두 줄로 합친다. */
export const headerAt = (grid: string[][], row: number): HeaderGuess => {
  const width = widthOf(grid);
  if (row < 0 || row >= grid.length) {
    const columns: ColumnMap = Array.from({ length: width }, (_, c) => NO_HEADER_COLUMNS[c] ?? null);
    return { headerRow: -1, headerRows: 1, labels: Array(width).fill(""), columns };
  }
  const top = Array.from({ length: width }, (_, c) => (grid[row][c] ?? "").trim());
  const below = grid[row + 1] ?? [];
  // 위 줄도 머리글다워야 한다 — 제목 줄 아래 머리글을 「두 줄 머리글」 로 오인하지 않게.
  const topKnown = top.filter((t) => t !== "" && guessOne(t) !== null).length;
  const isSub =
    topKnown >= 2 &&
    below.some((v) => has(normLabel(v ?? ""), SUBHEAD_WORDS)) &&
    below.every((v) => {
      const t = (v ?? "").trim();
      return t === "" || guessOne(t) !== null;
    });
  if (!isSub) return { headerRow: row, headerRows: 1, labels: top, columns: guessColumns(top) };

  const carried = carryRight(top, width);
  const labels = top.map((t, c) => {
    const sub = (below[c] ?? "").trim();
    if (sub === "") return t;
    const upper = t !== "" ? t : carried[c];
    return upper === "" ? sub : `${upper} ${sub}`;
  });
  return { headerRow: row, headerRows: 2, labels, columns: guessColumns(labels) };
};

/** 첫 20줄 중 알려진 머리글이 가장 많이 맞는 줄. 못 찾으면 headerRow -1. */
export const detectHeader = (grid: string[][]): HeaderGuess => {
  let best: HeaderGuess = headerAt(grid, -1);
  let bestScore = 1;
  const limit = Math.min(grid.length, HEADER_SCAN_ROWS);
  for (let r = 0; r < limit; r += 1) {
    const guess = headerAt(grid, r);
    const score = scoreOf(guess.columns);
    if (score > bestScore) {
      best = guess;
      bestScore = score;
    }
  }
  return best;
};

/** 자료가 시작하는 줄. */
export const dataStartOf = (header: Pick<HeaderGuess, "headerRow" | "headerRows">): number =>
  header.headerRow < 0 ? 0 : header.headerRow + header.headerRows;

// 항목 값을 담을 수 있는 단가·금액 열.
const PRICE_FIELDS = new Set<ImportField>([
  "material_unit_price",
  "labor_unit_price",
  "material_amount",
  "labor_amount",
  "expense_unit_price",
]);

const isBlankOrZero = (text: string): boolean => {
  const t = (text ?? "").trim();
  return t === "" || parseNumber(t) === 0;
};

/** 단가·금액 열이 전부 비고 「금액」 열에만 값이 있으면 그 열을 자재 금액으로 담는다(빈 단가·금액 열은 버림). 해당 없으면 받은 매핑 그대로. */
export const inferAmountColumn = (
  grid: string[][],
  columns: ColumnMap,
  dataStart: number
): ColumnMap => {
  const rows = grid.slice(Math.max(dataStart, 0));
  const priceCols = columns
    .map((f, i) => (f !== null && PRICE_FIELDS.has(f) ? i : -1))
    .filter((i) => i >= 0);
  if (priceCols.some((c) => rows.some((r) => !isBlankOrZero(r[c] ?? "")))) return columns;
  const amountCol = columns.findIndex(
    (f, c) => f === "amount_total" && rows.some((r) => (parseNumber(r[c] ?? "") ?? 0) > 0)
  );
  if (amountCol < 0) return columns;
  return columns.map((f, c) =>
    c === amountCol ? "material_amount" : priceCols.includes(c) ? null : f
  );
};

/** 격자 줄 index → 엑셀에 보이는 행 번호(1부터). -1(머리글 없음)은 0. */
export const excelRowOf = (rowNumbers: number[] | undefined, index: number): number =>
  index < 0 ? 0 : (rowNumbers?.[index] ?? index + 1);

/** 엑셀 행 번호 → 격자 줄 index. 0 은 -1(머리글 없음), 빈 행이면 그 아래 첫 줄, 끝을 넘으면 null. */
export const gridIndexOfExcelRow = (rowNumbers: number[], row: number): number | null => {
  if (row <= 0) return -1;
  const at = rowNumbers.findIndex((n) => n >= row);
  return at < 0 ? null : at;
};

// 「머리글 줄」 칸에 친 글자를 확정할 때 — 빈칸·숫자 아님·끝 넘음은 null(이전 값 유지), 0 은 -1(머리글 없음).
export const headerIndexOfInput = (text: string, rowNumbers: number[]): number | null => {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 0) return null;
  return gridIndexOfExcelRow(rowNumbers, n);
};

const SHEET_SKIP = ["갑지", "표지", "요약", "총괄", "집계", "개요"];
const SHEET_PREFER = ["상세", "내역", "을지", "견적"];

/** 기본으로 고를 시트 — 「상세」 > 「내역」 > 「을지」 > 「견적」 순, 없으면 줄이 가장 많은 시트. */
export const pickDefaultSheet = (names: string[], rowsOf: (name: string) => number): string => {
  const candidates = names.filter((n) => !SHEET_SKIP.some((w) => n.includes(w)));
  for (const word of SHEET_PREFER) {
    const hit = candidates.find((n) => n.includes(word));
    if (hit) return hit;
  }
  const pool = candidates.length > 0 ? candidates : names;
  return pool.reduce((best, n) => (rowsOf(n) > rowsOf(best) ? n : best), pool[0] ?? "");
};

// 원본 단가 기준 추정 — 공급가액·부가세 줄이 있으면 별도, 없으면 견적 방식을 그대로 따른다(바꾸지 않음).
export const guessSourceVatMode = (parsed: Pick<ImportParse, "vatSplitRows">, quoteMode: VatMode): VatMode =>
  parsed.vatSplitRows ? "EXCL" : quoteMode;
